import { useState, useEffect, useRef } from 'react';
import { useI18n } from '@react/hooks';
import {
  CORE_ASPECT_TYPES,
  getRecommendedAspectTypes,
  getAvailableAspectTypes,
  groupTypesByCategory,
} from '@/features/aspects/logic';
import type { AspectTypeConfig } from '@/features/aspects/types';
import type { Aspect } from '@core/types/task/Aspect';

function SearchIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" />
      <path d="M21 21l-4.35-4.35" />
    </svg>
  );
}

interface AspectCommandMenuProps {
  isOpen: boolean;
  onClose: () => void;
  entityType: string;
  attachedTypeNames: string[];
  onAttached: (aspect: Aspect) => void;
}

export function AspectCommandMenu({
  isOpen,
  onClose,
  entityType,
  attachedTypeNames,
  onAttached,
}: AspectCommandMenuProps) {
  const { t } = useI18n();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (isOpen) inputRef.current?.focus();
  }, [isOpen]);

  if (!isOpen) return null;

  const recommended = getRecommendedAspectTypes(entityType, CORE_ASPECT_TYPES);
  const available = getAvailableAspectTypes(CORE_ASPECT_TYPES, attachedTypeNames);
  const grouped = groupTypesByCategory(available);

  const filteredRecommended = query.trim()
    ? recommended.filter((t) =>
        t.typeName.includes(query.toLowerCase()) ||
        t.category.includes(query.toLowerCase()),
      )
    : recommended;

  const filteredGrouped = query.trim()
    ? grouped
        .map(([cat, types]) => [
          cat,
          types.filter(
            (tp) =>
              tp.typeName.includes(query.toLowerCase()) ||
              tp.category.includes(query.toLowerCase()),
          ),
        ] as [string, AspectTypeConfig[]])
        .filter(([, types]) => types.length > 0)
    : grouped;

  const handleAttach = (typeConfig: AspectTypeConfig) => {
    // Zero-click attach for types with no required fields
    const aspect: Aspect = {
      entity: { type_name: `${typeConfig.typeName}_aspect` } as any,
      urn: '',
      title: t(typeConfig.displayNameKey),
      subtitle: '',
      icon: typeConfig.icon,
      color: typeConfig.color,
      status: 'active',
    };
    onAttached(aspect);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[20vh]">
      <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />
      <div
        className="relative w-full max-w-lg mx-4 bg-rail border border-rail-border rounded-lg shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-label={t('rail.aspects.search')}
        onKeyDown={(e) => { if (e.key === 'Escape') onClose(); }}
      >
        {/* Search */}
        <div className="flex items-center gap-2 px-3 py-2.5 border-b border-rail-border">
          <SearchIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('rail.aspects.search')}
            className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none"
          />
          <kbd className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">ESC</kbd>
        </div>

        <div className="max-h-80 overflow-y-auto p-2">
          {/* Recommended section */}
          {filteredRecommended.length > 0 && (
            <>
              <div className="px-2 py-1">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">
                  {t('rail.aspects.recommended')}
                </p>
              </div>
              {filteredRecommended.map((typeConfig) => (
                <AspectTypeItem
                  key={typeConfig.typeName}
                  config={typeConfig}
                  onAttach={() => handleAttach(typeConfig)}
                />
              ))}
              <div className="border-t border-rail-border my-2" />
            </>
          )}

          {/* Browse catalog */}
          <div className="px-2 py-1">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">
              {t('rail.aspects.catalog')}
            </p>
          </div>

          {filteredGrouped.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-4">
              {t('layout.search.noResults')}
            </p>
          )}

          {filteredGrouped.map(([category, types]) => (
            <div key={category} className="mb-2">
              <p className="text-xs text-muted-foreground/70 px-3 py-1 capitalize">{category}</p>
              {types.map((typeConfig) => (
                <AspectTypeItem
                  key={typeConfig.typeName}
                  config={typeConfig}
                  onAttach={() => handleAttach(typeConfig)}
                />
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function AspectTypeItem({
  config,
  onAttach,
}: {
  config: AspectTypeConfig;
  onAttach: () => void;
}) {
  const { t } = useI18n();

  return (
    <button
      onClick={onAttach}
      className="flex items-center gap-3 w-full px-3 py-2.5 rounded-md text-left hover:bg-accent transition-colors"
    >
      <div
        className="h-8 w-8 rounded-md flex items-center justify-center flex-shrink-0 bg-muted"
      >
        <svg className={`h-4 w-4 ${config.color}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
          <path d={config.icon} />
        </svg>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground">{t(config.displayNameKey)}</p>
        <p className="text-xs text-muted-foreground">{t(config.descriptionKey)}</p>
      </div>
      <span className="text-xs text-primary flex-shrink-0">{t(config.actionVerbKey)}</span>
    </button>
  );
}
